import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Send, FileText, Loader2, Heart, Mic, Square, Waves, CheckCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { CoachPersona } from '../lib/personas';
import AvatarStack from './AvatarStack';
import styles from './ChatInterface.module.css';
import type { SessionFeedback } from '../types/session';
import { SessionFeedback as SessionFeedbackComponent } from './SessionFeedback';
import { API_BASE } from '../lib/api';
import ActionNudge from './ActionNudge';
import WrapUpPrompt from './WrapUpPrompt';
import EndSessionChip from './EndSessionChip';

const Waveform = () => (
  <div className="flex items-center justify-center h-12 w-12 rounded-full bg-rose-600/20 animate-pulse">
    <div className={`flex items-center gap-0.5 ${styles.animateWaveform}`}>
      <span className={`h-3 w-0.5 bg-rose-600 rounded-full ${styles.barDelay0}`}></span>
      <span className={`h-5 w-0.5 bg-rose-600 rounded-full ${styles.barDelay1}`}></span>
      <span className={`h-3 w-0.5 bg-rose-600 rounded-full ${styles.barDelay2}`}></span>
    </div>
  </div>
);

const VoiceStatus = ({ state, tone, message, showStop, onToggleRecord }: {
  state: 'idle' | 'recording' | 'processing' | 'sent' | 'error';
  tone: string;
  message: string;
  showStop: boolean;
  onToggleRecord: () => void;
}) => {
  const bgColor = tone === 'danger' ? 'bg-red-500/10 border-red-500 text-red-500'
    : tone === 'info' ? 'bg-blue-500/10 border-blue-500 text-blue-500'
      : tone === 'success' ? 'bg-green-500/10 border-green-500 text-green-500'
        : 'bg-yellow-500/10 border-yellow-500 text-yellow-500';

  return (
    <div className={`absolute top-4 right-4 flex items-center gap-2 px-3 py-2 rounded-full border ${bgColor} backdrop-blur-sm z-10`}>
      {state === 'recording' && <Waves className="h-4 w-4 animate-pulse" />}
      {state === 'processing' && <Loader2 className="h-4 w-4 animate-spin" />}
      {state === 'sent' && <CheckCircle className="h-4 w-4" />}
      <span className="text-sm font-medium">{message}</span>
      {showStop && (
        <button onClick={onToggleRecord} className="ml-2 p-1 hover:opacity-80" aria-label="Stop recording">
          <Square className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

export type CoachContribution = {
  coach: string;
  text: string;
};

export type ChatMessage = {
  id: string;
  text: string;
  sender: 'user' | 'ai' | 'system';
  timestamp?: string;
  coach?: string;
  source?: 'integrated' | 'coach';
  coachBreakdowns?: CoachContribution[];
};

type RenderMessage = {
  id: string;
  sender: ChatMessage['sender'];
  coach?: string;
  source?: ChatMessage['source'];
  chunks: string[];
  visibleChunks: number;
  coachBreakdowns?: CoachContribution[];
};

const MoMDisplay = lazy(() => import('./MoMDisplay'));

const CHUNK_DELAY_MS = 600;
const MAX_CHUNK_LENGTH = 320;
const TYPING_DOT_DELAYS = ['0ms', '140ms', '280ms'] as const;

const chunkMessage = (rawText: string): string[] => {
  // Safely handle various input types
  if (rawText === null || rawText === undefined) {
    console.warn('Received null/undefined message text');
    return [''];
  }

  // Convert to string if not already a string
  const safeText = typeof rawText === 'string' ? rawText : String(rawText);
  const text = safeText.trim();

  if (!text) {
    return [''];
  }

  try {
    const paragraphs = text.split(/\n{2,}/g).map(paragraph => paragraph.trim()).filter(Boolean);
    const chunks: string[] = [];

    paragraphs.forEach(paragraph => {
      if (!paragraph) return;

      // Handle lists separately
      if (/^[-*\d+\.]/m.test(paragraph)) {
        chunks.push(paragraph);
        return;
      }

      // Simple paragraphs
      if (paragraph.length <= MAX_CHUNK_LENGTH) {
        chunks.push(paragraph);
        return;
      }

      // Split long paragraphs
      const sentences = paragraph.split(/(\. |\? |\! )/).filter(Boolean);
      let currentChunk = '';

      sentences.forEach(sentence => {
        if (currentChunk.length + sentence.length > MAX_CHUNK_LENGTH) {
          if (currentChunk) {
            chunks.push(currentChunk);
            currentChunk = '';
          }

          // If a single sentence is too long, break it up
          if (sentence.length > MAX_CHUNK_LENGTH) {
            for (let i = 0; i < sentence.length; i += MAX_CHUNK_LENGTH) {
              chunks.push(sentence.slice(i, i + MAX_CHUNK_LENGTH));
            }
            return;
          }
        }

        currentChunk += sentence;
      });

      if (currentChunk) {
        chunks.push(currentChunk);
      }
    });

    return chunks.length ? chunks : [text];
  } catch (error) {
    console.error('Error chunking message:', error);
    return [text || ''];
  }
};

const markdownComponents: Components = {
  p: (props: ComponentPropsWithoutRef<'p'>) => <p {...props} className="mt-0 text-sm leading-relaxed last:mb-0" />,
  ul: (props: ComponentPropsWithoutRef<'ul'>) => (
    <ul {...props} className="mt-1 list-outside space-y-1 pl-5 text-sm leading-relaxed" />
  ),
  ol: (props: ComponentPropsWithoutRef<'ol'>) => (
    <ol {...props} className="mt-1 list-outside space-y-1 pl-5 text-sm leading-relaxed" />
  ),
  li: (props: ComponentPropsWithoutRef<'li'>) => <li {...props} className="marker:text-current" />,
  strong: (props: ComponentPropsWithoutRef<'strong'>) => (
    <strong {...props} className="font-semibold text-current" />
  ),
  em: (props: ComponentPropsWithoutRef<'em'>) => <em {...props} className="italic text-current" />,
};

const MoMSkeleton = () => (
  <div className="w-full animate-pulse rounded-2xl border border-border/60 bg-muted/40 p-4">
    <div className="h-4 w-32 rounded bg-muted" />
    <div className="mt-4 space-y-2">
      <div className="h-3 w-3/4 rounded bg-muted" />
      <div className="h-3 w-2/3 rounded bg-muted" />
      <div className="h-3 w-1/2 rounded bg-muted" />
    </div>
  </div>
);

const ChatSkeletonRow = ({ align = 'left' }: { align?: 'left' | 'right' }) => (
  <div className={`flex items-start gap-3 ${align === 'right' ? 'justify-end' : ''}`}>
    {align === 'left' && <div className="h-8 w-8 shrink-0 rounded-full bg-muted" />}
    <div className={`max-w-[75%] space-y-2 ${align === 'right' ? 'items-end text-right' : ''}`}>
      <div className="h-3 w-20 rounded bg-muted/80" />
      <div className="h-4 rounded-lg bg-muted/70" />
      <div className="h-4 w-1/2 rounded-lg bg-muted/50" />
    </div>
    {align === 'right' && <div className="h-8 w-8 shrink-0 rounded-full bg-muted" />}
  </div>
);

type ChatInterfaceProps = {
  isSessionLoading: boolean;
  messages: ChatMessage[];
  onSend: (message: string) => Promise<void> | void;
  isSending: boolean;
  onGenerateMom: () => void;
  isGeneratingMom: boolean;
  momSummary: any;
  activeCoaches: string[];
  personaMap: Record<string, CoachPersona>;
  likedMessageIds: string[];
  onToggleLike: (message: ChatMessage) => void;
  sessionTitle?: string;
  sessionUpdatedAt?: string | null;
  momReady: boolean;
  onToggleCoach: (coachId: string) => void;
  onSubmitFeedback?: (score: number, comment: string) => Promise<void> | void;
  feedback?: SessionFeedback | null;
  isSubmittingFeedback?: boolean;
  feedbackError?: string | null;
  isTyping: boolean;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSendMessage: (message: string) => void;
  sessionId: string;
  onSessionEnd?: () => void;
};

const ChatInterface = ({
  isSessionLoading,
  messages,
  onSend,
  isSending,
  onGenerateMom,
  isGeneratingMom,
  momSummary,
  activeCoaches,
  personaMap,
  likedMessageIds,
  onToggleLike,
  sessionTitle,
  sessionUpdatedAt,
  momReady,
  onToggleCoach,
  onSubmitFeedback,
  feedback,
  isSubmittingFeedback = false,
  feedbackError,
  isTyping,
  inputValue,
  onInputChange,
  onSendMessage,
  sessionId,
  onSessionEnd,
}: ChatInterfaceProps) => {
  const { getToken } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
  const [showSentConfirmation, setShowSentConfirmation] = useState(false);
  
  // Smart nudging state
  const [actionNudgeMessage, setActionNudgeMessage] = useState<ChatMessage | null>(null);
  const [actionNudgeCount, setActionNudgeCount] = useState(0);
  const [showWrapUpPrompt, setShowWrapUpPrompt] = useState(false);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [dismissedNudges, setDismissedNudges] = useState<Set<string>>(new Set());
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<number | null>(null);
  const recordingMimeTypeRef = useRef<string>('');
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const chunkTimerRef = useRef<number | null>(null);
  const [renderMessages, setRenderMessages] = useState<RenderMessage[]>([]);
  // Feedback state is now managed in the SessionFeedback component

  // Adapter: support MoM v2-slim (observationalSummary + goldenInsights + actionItems)
  const adaptedMoM = useMemo(() => {
    if (!momSummary) return null;
    let raw: any = momSummary;
    try {
      if (typeof raw === 'string') raw = JSON.parse(raw);
    } catch {
      // keep as-is if not parseable
    }
    if (raw && raw.version === 'mom.v2slim') {
      const actions = Array.isArray(raw.actionItems) ? raw.actionItems : [];
      return {
        title: 'Session Summary',
        summary: typeof raw.observationalSummary === 'string' ? raw.observationalSummary : '',
        keyTakeaways: Array.isArray(raw.goldenInsights) ? raw.goldenInsights : [],
        actionLedger: actions.map((a: any, idx: number) => ({
          id: a?.id || `act_${idx + 1}`,
          label: String(a?.label || ''),
          priority: a?.priority,
          status: a?.status,
          timeframe: a?.timeframe,
          dueDate: a?.dueDate,
          metric: a?.metric,
        })),
        analytics: raw.analytics,
      } as any;
    }
    return raw;
  }, [momSummary]);

  const computeChunks = useCallback((message: ChatMessage) => {
    const baseChunks = chunkMessage(message.text);
    return message.sender === 'ai' ? baseChunks : [message.text];
  }, []);

  // Feedback state is now managed in the SessionFeedback component

  const messageLookup = useMemo(() => {
    const map = new Map<string, ChatMessage>();
    messages?.forEach((message) => map.set(message.id, message));
    return map;
  }, [messages]);

  const hasMessages = (messages?.length ?? 0) > 0;
  const likedMessageSet = useMemo(() => new Set(likedMessageIds), [likedMessageIds]);

  // Action detection logic
  const detectActionableMessage = useCallback((message: ChatMessage): boolean => {
    if (message.sender !== 'ai') return false;
    
    const actionKeywords = [
      'next step', 'action', 'try', 'commit', 'plan', 'task', 'do this',
      'recommend', 'suggest', 'should', 'could', 'might want to',
      'consider', 'focus on', 'work on', 'start with'
    ];
    
    const text = message.text.toLowerCase();
    return actionKeywords.some(keyword => text.includes(keyword)) ||
           /^[-*•]\s/.test(message.text) || // Bullet points
           /\d+\.\s/.test(message.text);   // Numbered lists
  }, []);

  // Check for closing cues in user messages
  const hasClosingCues = useCallback((text: string): boolean => {
    const closingKeywords = [
      'thanks', 'thank you', 'that helps', 'wrap up', 'summary',
      'good session', 'appreciate', 'helpful', 'got it', 'clear now'
    ];
    
    const lowerText = text.toLowerCase();
    return closingKeywords.some(keyword => lowerText.includes(keyword));
  }, []);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      if (chunkTimerRef.current) {
        window.clearInterval(chunkTimerRef.current);
        chunkTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!messages || messages.length === 0) {
      setRenderMessages([]);
      return;
    }

    setRenderMessages((previous) => {
      const previousMap = new Map(previous.map((item) => [item.id, item] as const));
      const isFirstHydration = previous.length === 0;

      return messages.map((message, index) => {
        const existing = previousMap.get(message.id);
        const chunks = computeChunks(message);

        if (existing) {
          return {
            ...existing,
            sender: message.sender,
            coach: message.coach,
            source: message.source,
            chunks,
            visibleChunks: Math.min(existing.visibleChunks, chunks.length),
          } satisfies RenderMessage;
        }

        const shouldAnimate = !isFirstHydration && message.sender === 'ai' && index === messages.length - 1;
        const visibleChunks = message.sender === 'ai' ? (shouldAnimate ? 0 : chunks.length) : chunks.length;

        return {
          id: message.id,
          sender: message.sender,
          coach: message.coach,
          source: message.source,
          chunks,
          visibleChunks,
        } satisfies RenderMessage;
      });
    });
  }, [messages, computeChunks]);

  useEffect(() => {
    const active = renderMessages.find((item) => item.sender === 'ai' && item.visibleChunks < item.chunks.length);

    if (!active) {
      if (chunkTimerRef.current) {
        window.clearInterval(chunkTimerRef.current);
        chunkTimerRef.current = null;
      }
      return;
    }

    if (chunkTimerRef.current) {
      return;
    }

    chunkTimerRef.current = window.setInterval(() => {
      setRenderMessages((previous) => {
        let didUpdate = false;
        const next = previous.map((item) => {
          if (item.id === active.id && item.visibleChunks < item.chunks.length) {
            didUpdate = true;
            return { ...item, visibleChunks: item.visibleChunks + 1 } satisfies RenderMessage;
          }
          return item;
        });

        if (!didUpdate && chunkTimerRef.current) {
          window.clearInterval(chunkTimerRef.current);
          chunkTimerRef.current = null;
        }

        return next;
      });
    }, CHUNK_DELAY_MS);

    return () => {
      if (chunkTimerRef.current) {
        window.clearInterval(chunkTimerRef.current);
        chunkTimerRef.current = null;
      }
    };
  }, [renderMessages]);

  // Smart nudging effects
  useEffect(() => {
    if (!messages || messages.length < 5) return;
    
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.sender !== 'ai') return;
    
    // Check if we should show action nudge
    if (messages.length >= 5 && 
        actionNudgeCount < 2 && 
        !dismissedNudges.has(lastMessage.id) &&
        detectActionableMessage(lastMessage)) {
      setActionNudgeMessage(lastMessage);
    }
  }, [messages, actionNudgeCount, dismissedNudges, detectActionableMessage]);

  // Wrap-up prompt logic
  useEffect(() => {
    if (!messages || messages.length < 8 || showWrapUpPrompt || momReady) return;
    
    const lastUserMessage = [...messages].reverse().find(m => m.sender === 'user');
    if (lastUserMessage && hasClosingCues(lastUserMessage.text)) {
      setShowWrapUpPrompt(true);
      return;
    }
    
    // Inactivity check
    const checkInactivity = () => {
      const now = Date.now();
      if (now - lastActivityTime > 180000) { // 3 minutes
        setShowWrapUpPrompt(true);
      }
    };
    
    const timer = setTimeout(checkInactivity, 5000);
    return () => clearTimeout(timer);
  }, [messages, showWrapUpPrompt, momReady, lastActivityTime, hasClosingCues]);

  // Update activity time on user interaction
  useEffect(() => {
    const updateActivity = () => setLastActivityTime(Date.now());
    
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => document.addEventListener(event, updateActivity));
    
    return () => {
      events.forEach(event => document.removeEventListener(event, updateActivity));
    };
  }, []);

  const scrollToLatest = useCallback(() => {
    const node = scrollContainerRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!renderMessages.length) return;
    const frame = window.requestAnimationFrame(scrollToLatest);
    return () => window.cancelAnimationFrame(frame);
  }, [renderMessages, scrollToLatest]);

  
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingSeconds(0);
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  }, []);

  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || isSending || isTranscribing) return;
    // Always stop any active recording/transcription
    if (isRecording) {
      stopRecording();
    }
    setIsTranscribing(false);
    setTranscriptionError(null);
    await Promise.resolve(onSend(inputValue.trim()));
    onInputChange('');
    setShowSentConfirmation(true);
    setTimeout(() => setShowSentConfirmation(false), 2000);
  }, [inputValue, isSending, isTranscribing, isRecording, stopRecording, onSend, onInputChange]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Determine the best supported MIME type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/ogg')
          ? 'audio/ogg'
          : 'audio/wav';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      recordingMimeTypeRef.current = mimeType;
      recordedChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(recordedChunksRef.current, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());

        // Transcribe the audio
        await transcribeAudio(audioBlob);
      };

      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      setTranscriptionError('Microphone access denied');
      setTimeout(() => setTranscriptionError(null), 3000);
    }
  }, []);

  
  const transcribeAudio = useCallback(async (audioBlob: Blob) => {
    setIsTranscribing(true);
    setTranscriptionError(null);

    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await fetch(`${API_BASE}/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.message || data.error || `Transcription failed: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      const transcribedText = data.text;

      if (transcribedText) {
        onInputChange(transcribedText);
      }
    } catch (error) {
      console.error('Transcription failed:', error);
      const message = error instanceof Error ? error.message : 'Failed to transcribe audio. Please try typing instead.';
      setTranscriptionError(message);
      setTimeout(() => setTranscriptionError(null), 5000);
    } finally {
      setIsTranscribing(false);
    }
  }, []);

  const handleRecordToggle = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  const hasSessionEnded = momReady || (messages?.length ?? 0) > 0;

  const voiceStatus = useMemo(() => {
    if (isRecording) {
      const minutes = String(Math.floor(recordingSeconds / 60)).padStart(2, '0');
      const seconds = String(recordingSeconds % 60).padStart(2, '0');
      return { state: 'recording' as const, tone: 'danger' as const, message: `Recording… ${minutes}:${seconds}`, showStop: true };
    }
    if (isTranscribing) {
      return { state: 'processing' as const, tone: 'info' as const, message: 'Transcribing…', showStop: false };
    }
    if (showSentConfirmation) {
      return { state: 'sent' as const, tone: 'success' as const, message: 'Message sent!', showStop: false };
    }
    if (transcriptionError) {
      return { state: 'error' as const, tone: 'warn' as const, message: transcriptionError, showStop: false };
    }
    return { state: 'idle' as const, tone: 'primary' as const, message: 'Click to speak', showStop: false };
  }, [isRecording, isTranscribing, transcriptionError, recordingSeconds, showSentConfirmation]);

  const momStatusLabel = momReady ? 'MoM Available' : 'No MoM Yet';
  const momStatusTone = momReady ? 'border-green-500 bg-green-500/10 text-green-500' : 'border-muted-foreground bg-muted';

  // Nudge handlers
  const handleActionNudgeDismiss = useCallback(() => {
    if (actionNudgeMessage) {
      setDismissedNudges(prev => new Set([...prev, actionNudgeMessage.id]));
      setActionNudgeMessage(null);
    }
  }, [actionNudgeMessage]);

  const handleActionNudgeSave = useCallback(() => {
    setActionNudgeCount(prev => prev + 1);
    setActionNudgeMessage(null);
  }, []);

  const handleWrapUpDismiss = useCallback(() => {
    setShowWrapUpPrompt(false);
  }, []);

  const handleMoMGenerated = useCallback(() => {
    setShowWrapUpPrompt(false);
    onGenerateMom();
  }, [onGenerateMom]);

  const handleSessionEnd = useCallback(() => {
    if (onSessionEnd) {
      onSessionEnd();
    }
  }, [onSessionEnd]);

  return (
    <>
      {/* Wrap-up Prompt */}
      {showWrapUpPrompt && (
        <WrapUpPrompt
          sessionId={sessionId}
          onDismiss={handleWrapUpDismiss}
          onMoMGenerated={handleMoMGenerated}
        />
      )}
      
      <div className="relative flex h-full min-h-[520px] flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-lg">
        {/* Header with End Session Chip */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${momStatusTone}`}>
              <FileText className="h-3.5 w-3.5" />
              {momStatusLabel}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <EndSessionChip
              sessionId={sessionId}
              messageCount={messages?.length || 0}
              onSessionEnd={handleSessionEnd}
            />
            <AvatarStack
              personas={Object.values(personaMap)}
              selectedIds={activeCoaches}
              onToggle={onToggleCoach}
            />
          </div>
        </div>
        {voiceStatus.state !== 'idle' && (
          <VoiceStatus
            state={voiceStatus.state}
            tone={voiceStatus.tone}
            message={voiceStatus.message}
            showStop={voiceStatus.showStop}
            onToggleRecord={handleRecordToggle}
          />
        )}
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4" ref={scrollContainerRef}>
          {isSessionLoading ? (
            <div className="space-y-4">
              <ChatSkeletonRow align="left" />
              <ChatSkeletonRow align="right" />
              <ChatSkeletonRow align="left" />
            </div>
          ) : (
            <div className="space-y-4">
              {renderMessages.map((message, index) => {
                const originalMessage = messageLookup.get(message.id);
                if (!originalMessage) return null;
                
                const isLiked = likedMessageSet.has(message.id);
                const isLastAiMessage = message.sender === 'ai' && index === renderMessages.length - 1;
                
                return (
                  <div key={message.id} className={`flex items-start gap-3 ${message.sender === 'user' ? 'justify-end' : ''}`}>
                    {message.sender === 'ai' && (
                      <div className="h-8 w-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary">
                          {message.coach ? personaMap[message.coach]?.name?.charAt(0) || 'AI' : 'AI'}
                        </span>
                      </div>
                    )}
                    
                    <div className={`max-w-[75%] space-y-2 ${message.sender === 'user' ? 'items-end text-right' : ''}`}>
                      <div className={`rounded-2xl px-4 py-3 ${
                        message.sender === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {message.chunks.slice(0, message.visibleChunks).map((chunk, chunkIndex) => (
                          <div key={chunkIndex} className="mb-2 last:mb-0">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={markdownComponents}
                            >
                              {chunk}
                            </ReactMarkdown>
                          </div>
                        ))}
                        
                        {message.visibleChunks < message.chunks.length && (
                          <div className="flex items-center gap-1 mt-2">
                            {TYPING_DOT_DELAYS.map((delay, i) => (
                              <div
                                key={i}
                                className="h-1 w-1 rounded-full bg-current animate-pulse"
                                style={{ animationDelay: delay }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {message.sender === 'ai' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onToggleLike(originalMessage)}
                            className={`p-1 rounded-full transition-colors ${
                              isLiked ? 'text-red-500 hover:text-red-600' : 'text-muted-foreground hover:text-foreground'
                            }`}
                            aria-label={isLiked ? 'Unlike message' : 'Like message'}
                          >
                            <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
                          </button>
                        </div>
                      )}
                      
                      {/* Action Nudge */}
                      {actionNudgeMessage?.id === message.id && (
                        <ActionNudge
                          message={originalMessage}
                          sessionId={sessionId}
                          onDismiss={handleActionNudgeDismiss}
                          onSave={handleActionNudgeSave}
                        />
                      )}
                    </div>
                    
                    {message.sender === 'user' && (
                      <div className="h-8 w-8 shrink-0 rounded-full bg-muted flex items-center justify-center">
                        <span className="text-xs font-semibold text-muted-foreground">You</span>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {isTyping && (
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-semibold text-primary">AI</span>
                  </div>
                  <div className="bg-muted rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-1">
                      {TYPING_DOT_DELAYS.map((delay, i) => (
                        <div
                          key={i}
                          className="h-1 w-1 rounded-full bg-current animate-pulse"
                          style={{ animationDelay: delay }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Input Area */}
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Type your message..."
                className="w-full rounded-2xl border border-input bg-background px-4 py-3 pr-12 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                disabled={isSending || isTranscribing}
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isSending || isTranscribing}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </div>
            
            <button
              className={`rounded-full p-3 transition-all duration-200 ${
                isRecording
                  ? 'bg-red-500 hover:bg-red-600 animate-pulse text-white'
                  : isTranscribing
                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
              onClick={handleRecordToggle}
              disabled={isTranscribing}
              aria-label={isRecording ? 'Stop recording' : isTranscribing ? 'Processing...' : 'Start recording'}
              title={isRecording ? 'Recording…' : isTranscribing ? 'Transcribing…' : 'Ready for next recording'}
            >
              {isTranscribing ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isRecording ? (
                <Square className="h-5 w-5" />
              ) : showSentConfirmation ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <Mic className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <div className="p-4">
          {adaptedMoM ? (
            <Suspense fallback={<MoMSkeleton />}>
              <MoMDisplay summary={adaptedMoM} />
            </Suspense>
          ) : (
            <div className="flex items-center justify-between rounded-xl border border-border/60 bg-muted/30 p-4">
              <div className="text-sm text-muted-foreground">No MoM yet for this session.</div>
              <button
                type="button"
                onClick={onGenerateMom}
                disabled={isGeneratingMom}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
              >
                {isGeneratingMom ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Generating…
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4" /> Generate MoM
                  </>
                )}
              </button>
            </div>
          )}

          {hasSessionEnded && onSubmitFeedback && (
            <SessionFeedbackComponent
              className="mt-4"
              onSubmit={onSubmitFeedback}
              feedback={feedback}
              isSubmitting={isSubmittingFeedback}
              error={feedbackError}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ChatInterface;

// ...
